import { useCallback, type Dispatch, type SetStateAction } from 'react';

import { Alert } from '@components/Alert';
import { LocalFile } from '@graphql/generated';
import { useFilePicker } from '@utils/filePicker';
import { logError } from '@utils/logNonFatalError';

interface UseFilePickerHandlersOptions {
  allFilesCount: number;
  maxCount: number;
  setNoNeedShowDiscard: (value: boolean) => void;
  setLocalFiles: Dispatch<SetStateAction<LocalFile[]>>;
  setLocalImages: Dispatch<SetStateAction<LocalFile[]>>;
  handleUploadAndUpdate: (
    filesToUpload: LocalFile[],
    isImage: boolean
  ) => Promise<void>;
  navigation: { navigate: (screen: string, params?: object) => void };
  showLimitReached: () => void;
  // Dismisses the host BottomSheetModal so its portal-rendered content
  // doesn't stay layered over the camera preview once it's pushed on top.
  dismissBottomSheetForCamera?: () => void;
}

/**
 * Shared file/image picker and camera handlers for BottomSheet forms.
 * Extracts the identical pickFiles/pickImages/launchCamera logic from
 * TaskCreateBottomSheet, ProjectCreateBottomSheet, and ProjectTaskBottomSheet.
 */
export const useFilePickerHandlers = ({
  allFilesCount,
  maxCount,
  setNoNeedShowDiscard,
  setLocalFiles,
  setLocalImages,
  handleUploadAndUpdate,
  navigation,
  showLimitReached,
  dismissBottomSheetForCamera,
}: UseFilePickerHandlersOptions) => {
  const { launchImageSelection, launchFileSelection } = useFilePicker();

  const pickFiles = useCallback(async () => {
    const selectableDocumentsCount = maxCount - allFilesCount;

    if (selectableDocumentsCount <= 0) {
      showLimitReached();
      return;
    }
    setNoNeedShowDiscard(true);

    try {
      const r = await launchFileSelection(selectableDocumentsCount, true);

      if (!r || !Array.isArray(r) || r.length === 0) {
        setNoNeedShowDiscard(false);
        return;
      }

      setLocalFiles((prev) => [...prev, ...r]);
      const toUpload = r.filter((f) => !f.blobId || f.blobId === '');
      if (toUpload.length > 0) {
        await handleUploadAndUpdate(toUpload, false);
      }
    } catch (error) {
      logError({
        message: 'File picker error',
        errorMessage: error instanceof Error ? error.message : String(error),
        operationName: 'pickFiles',
      });
      Alert.alert('Selection error');
    }
    setNoNeedShowDiscard(false);
  }, [
    allFilesCount,
    maxCount,
    setNoNeedShowDiscard,
    setLocalFiles,
    handleUploadAndUpdate,
    showLimitReached,
    launchFileSelection,
  ]);

  const pickImages = useCallback(async () => {
    const selectableImagesCount = maxCount - allFilesCount;

    if (selectableImagesCount <= 0) {
      showLimitReached();
      return;
    }

    setNoNeedShowDiscard(true);
    try {
      const r = await launchImageSelection(selectableImagesCount, true);
      if (r) {
        setLocalImages((prev) => [...prev, ...r]);
        const toUpload = r.filter(
          (f: LocalFile) => !f.blobId || f.blobId === ''
        );
        if (toUpload.length > 0) {
          await handleUploadAndUpdate(toUpload, true);
        }
      }
    } catch (error) {
      logError({
        message: 'Image picker error',
        errorMessage: error instanceof Error ? error.message : String(error),
        operationName: 'pickImages',
      });
      Alert.alert('Selection error');
    } finally {
      setNoNeedShowDiscard(false);
    }
  }, [
    allFilesCount,
    maxCount,
    setNoNeedShowDiscard,
    setLocalImages,
    handleUploadAndUpdate,
    showLimitReached,
    launchImageSelection,
  ]);

  const launchCamera = useCallback(() => {
    const selectableImagesCount = maxCount - allFilesCount;

    if (selectableImagesCount > 0) {
      setNoNeedShowDiscard(true);
      dismissBottomSheetForCamera?.();
      setTimeout(() => {
        (navigation as any).navigate('expo-camera-photo-add-file', {
          onCallback: async (imageList: LocalFile[]) => {
            setNoNeedShowDiscard(false);
            if (imageList && imageList.length > 0) {
              setLocalImages((prev) => [...prev, ...imageList]);
              const toUpload = imageList.filter(
                (f) => !f.blobId || f.blobId === ''
              );
              if (toUpload.length > 0) {
                await handleUploadAndUpdate(toUpload, true);
              }
            }
          },
          photoCount: selectableImagesCount,
        });
      }, 500);
    } else {
      showLimitReached();
    }
  }, [
    allFilesCount,
    maxCount,
    setNoNeedShowDiscard,
    setLocalImages,
    handleUploadAndUpdate,
    navigation,
    showLimitReached,
    dismissBottomSheetForCamera,
  ]);

  return { pickFiles, pickImages, launchCamera };
};
